import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageList from '../../templates/PageList';
import FilterBar, { AppliedFilter, FilterSchema } from '../collateral/shared/FilterBar';
import { ATableHeader } from '../../organisms/ATable';
import StatusReasonModal from './StatusReasonModal';
import TemplateKindTabs from './template/TemplateKindTabs';
import TemplateRow, { TemplateRowAction } from './template/TemplateRow';
import { useTemplateList } from './template/useTemplateList';
import communicationApi from '../../../services/communicationApi';
import { Template } from '../../../types/communication';

const LINK_OPTIONS = [
    { value: 'FREE', label: 'Livres' },
    { value: 'LINKED', label: 'Em composição' },
];

const STATUS_OPTIONS = [
    { value: 'ACTIVE', label: 'Ativo' },
    { value: 'INACTIVE', label: 'Inativo' },
];

const TABLE_HEADERS: ATableHeader[] = [
    { id: 't-name', title: 'Modelo', field: 'name', canSort: true },
    { id: 't-usage', title: 'Usado em', field: 'usage', canSort: false, width: '280px' },
    { id: 't-status', title: 'Status', field: 'status', canSort: false, width: '90px' },
    { id: 't-actions', title: '', field: '', canSort: false, width: '56px' },
];

const TemplatePageList = () => {
    const navigate = useNavigate();
    const { page = '0', pageSize = '20', field = 'name', order = 'ASC' } = useParams();
    const pageNum = Number(page);
    const sizeNum = Number(pageSize);
    const [searchParams] = useSearchParams();
    const initialSearch = searchParams.get('search') ?? '';
    const list = useTemplateList({ page: pageNum, pageSize: sizeNum, field, order: order === 'DESC' ? 'DESC' : 'ASC', initialSearch });
    const [toggleTarget, setToggleTarget] = useState<Template | null>(null);
    const [searchInput, setSearchInput] = useState(initialSearch);
    const { filters } = list;

    const filterSchema: FilterSchema[] = [
        { key: 'link', label: 'Uso', type: 'enum', options: LINK_OPTIONS },
        { key: 'status', label: 'Status', type: 'enum', options: STATUS_OPTIONS },
    ];

    const initialFilters: AppliedFilter[] = [
        ...(filters.link ? [{ key: 'link', value: filters.link }] : []),
        ...(filters.status ? [{ key: 'status', value: filters.status }] : []),
    ];

    const onFilterBarSearch = (applied: AppliedFilter[]) => {
        const byKey = Object.fromEntries(applied.map((a) => [a.key, a.value]));
        list.updateFilters({
            link: (byKey.link as 'FREE' | 'LINKED' | '') ?? '',
            status: (byKey.status as 'ACTIVE' | 'INACTIVE' | '') ?? '',
        });
        resetPage();
    };

    const urlNavigate = (p: number, s: number, f: string, o: string) => `/communication/templates/${p}/${s}/${f}/${o}`;
    const resetPage = () => navigate(urlNavigate(0, sizeNum, field, order), { replace: true });

    const handleAction = async (template: Template, action: TemplateRowAction) => {
        const composition = list.compositionOf(template);
        if (action === 'open') navigate(`/communication/templates/item/${template.id}`);
        if (action === 'edit') {
            navigate(
                composition
                    ? `/communication/message-composition/${composition.id}/edit?template=${template.id}`
                    : `/communication/templates/item/${template.id}/edit`,
            );
        }
        if (action === 'toggle') setToggleTarget(template);
        if (action === 'duplicate') {
            try {
                const copy = await communicationApi.duplicateTemplate(template.id);
                toast.success('Modelo duplicado. A cópia está livre para editar.');
                navigate(`/communication/templates/item/${copy.id}/edit`);
            } catch {
                toast.error('Falha ao duplicar.');
            }
        }
    };

    const renderRows = () => {
        if (!list.rows.length) {
            return (
                <tr>
                    <td colSpan={TABLE_HEADERS.length} className="py-12 px-2 text-center">
                        <div className="text-sm font-semibold text-gray-600 dark:text-gray-300">Nenhum modelo encontrado</div>
                        <div className="text-xs text-gray-400 mt-1">Ajuste os filtros ou crie um modelo novo.</div>
                    </td>
                </tr>
            );
        }
        return list.rows.map((template, index) => (
            <TemplateRow
                key={template.id}
                template={template}
                composition={list.compositionOf(template)}
                striped={index % 2 === 0}
                onOpen={() => navigate(`/communication/templates/item/${template.id}`)}
                onAction={(action) => handleAction(template, action)}
            />
        ));
    };

    return (
        <>
            <PageList
                pageTitle="Modelos"
                url=""
                breadcrumb={[
                    { label: 'Comunicação', url: '#' },
                    { label: 'Modelos', url: '#' },
                ]}
                tableFieldSort={field}
                tableOrderSort={order as 'ASC' | 'DESC'}
                onTableSort={(f, o) => navigate(urlNavigate(pageNum, sizeNum, f, o), { replace: true })}
                renderRows={renderRows as any}
                page={pageNum}
                pageSize={sizeNum}
                onPageChange={(p) => navigate(urlNavigate(p, sizeNum, field, order), { replace: true })}
                onItemsPerPageChange={(s) => navigate(urlNavigate(0, s, field, order), { replace: true })}
                itemsLabel="modelos"
                defaultBackendHeader={false}
                tableHeaders={TABLE_HEADERS}
                isLoadingData={list.loading}
                externalTotalElements={list.total}
                externalTotalPages={list.totalPages}
            >
                {{
                    controlSlot: (
                        <div className="flex justify-end m-2">
                            <button
                                className="text-white bg-blue-500 hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded text-sm px-5 py-2.5 text-center whitespace-nowrap"
                                onClick={() => navigate('/communication/templates/new')}
                            >
                                Novo modelo
                            </button>
                        </div>
                    ),
                    filterSlot: (
                        <FilterBar
                            schema={filterSchema}
                            initialFilters={initialFilters}
                            onSearch={onFilterBarSearch}
                            searchValue={searchInput}
                            onSearchChange={setSearchInput}
                            onSearchSubmit={(value?: string) => {
                                list.updateFilters({ search: value ?? searchInput });
                                resetPage();
                            }}
                            searchPlaceholder="Procurar por nome ou descrição..."
                            searchInputClassName="w-72"
                            loading={list.loading}
                            leadingSlot={
                                <TemplateKindTabs
                                    value={filters.kind}
                                    counts={list.kindCounts}
                                    onChange={(kind) => {
                                        list.updateFilters({ kind });
                                        resetPage();
                                    }}
                                />
                            }
                        />
                    ),
                }}
            </PageList>
            <StatusReasonModal
                isOpen={Boolean(toggleTarget)}
                title={toggleTarget?.status === 'ACTIVE' ? 'Desativar modelo' : 'Ativar modelo'}
                actionLabel={toggleTarget?.status === 'ACTIVE' ? 'Desativar' : 'Ativar'}
                onCancel={() => setToggleTarget(null)}
                onConfirm={async (reason) => {
                    if (toggleTarget) {
                        try {
                            await communicationApi.toggleTemplateStatus(toggleTarget.id, reason);
                            toast.success(toggleTarget.status === 'ACTIVE' ? 'Modelo desativado.' : 'Modelo ativado.');
                            list.reload();
                        } catch (e) {
                            toast.error(e instanceof Error ? e.message : 'Falha ao atualizar status.');
                        }
                    }
                    setToggleTarget(null);
                }}
            />
        </>
    );
};

export default TemplatePageList;
